import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '../config/app-config';

/** Structured properties attached to a telemetry item. */
export type TelemetryProperties = Record<string, string | number | boolean>;

/**
 * Application Insights browser telemetry seam (T-060, Tech plan §7).
 *
 * Telemetry is enabled only when an App Insights connection string is configured;
 * otherwise every method is a no-op so local/test runs stay quiet and no secret is
 * required. The concrete `@microsoft/applicationinsights-web` SDK is wired behind
 * this service at deployment time (tracked under KI-09) — the app depends only on
 * this abstraction, mirroring the server-side telemetry seam.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private readonly config = inject(APP_CONFIG);

  get enabled(): boolean {
    return !!this.config.appInsights.connectionString;
  }

  trackException(error: Error, correlationId?: string): void {
    if (!this.enabled) {
      return;
    }
    this.send('exception', { message: error.message, correlationId: correlationId ?? '' });
  }

  trackEvent(name: string, properties?: TelemetryProperties): void {
    if (!this.enabled) {
      return;
    }
    this.send(name, properties ?? {});
  }

  trackPageView(name: string): void {
    if (!this.enabled) {
      return;
    }
    this.send('pageView', { name });
  }

  private send(_name: string, _properties: TelemetryProperties): void {
    // Deferred: forwarded to the App Insights SDK once configured (KI-09).
  }
}
